import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, MapPin, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ScheduleCallModal from '@/components/modals/ScheduleCallModal';

const ctaItems = [
  {
    icon: Phone,
    title: 'Get In Touch',
    description: 'Connect with our team to discuss your flooring needs and get expert guidance on the best solutions for your space.',
    cta: { label: 'Contact Us', href: '/contact' },
    action: 'link'
  },
  {
    icon: MapPin,
    title: 'Site Survey',
    description: 'Schedule a comprehensive site survey. Our experts will assess your space and provide tailored recommendations.',
    cta: { label: 'Schedule a Call' },
    action: 'schedule'
  },
  {
    icon: Lightbulb,
    title: 'Tailored Solutions',
    description: 'Explore our industry-specific flooring solutions designed to meet the unique challenges of your business.',
    cta: { label: 'View Solutions', href: '/solutions' },
    action: 'link'
  }
];

export const FinalCTASection = () => {
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);

  return (
    <section className="section-padding bg-muted">
      <div className="container-portico">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            Ready for Flooring That Lasts?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Take the first step toward flooring that performs. Our team is ready to help 
            you find the perfect solution.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {ctaItems.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group"
            >
              <div className="bg-card rounded-xl p-8 h-full flex flex-col items-center text-center border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-smooth">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary transition-colors duration-smooth">
                  <item.icon className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <h3 className="font-heading font-semibold text-xl mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground flex-grow mb-6">
                  {item.description}
                </p>
                {item.action === 'schedule' ? (
                  <Button
                    variant="outline"
                    className="w-full group/btn"
                    onClick={() => setIsCallModalOpen(true)}
                    data-testid="schedule-call-cta-home"
                  >
                    {item.cta.label}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                ) : (
                  <Link to={item.cta.href}>
                    <Button variant="outline" className="w-full group/btn">
                      {item.cta.label}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <ScheduleCallModal isOpen={isCallModalOpen} onClose={() => setIsCallModalOpen(false)} />
    </section>
  );
};

export default FinalCTASection;
